import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

interface FicheSoin {
  id: string;
  patient_id: string;
  medecin_id: string;
  date_soin: string;
  soins_a_administrer: string;
  statut: string;
  patients: { nom: string; prenom: string; numero_dossier: string } | null;
  medecins: { nom: string; prenom: string; specialite: string } | null;
}

interface FicheSurveillance {
  id: string;
  fiche_soin_id: string;
  patient_id: string;
  medecin_traitant_id: string;
  date_heure: string;
  infirmier_id: string;
  tension: string;
  temperature: string;
  pouls: string;
  saturation: string;
  conscience: string;
  traitement_adminstre: string;
  observations: string;
  infirmiers: { nom: string; prenom: string } | null;
}

export default function InfirmierDashboardPage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [infirmierId, setInfirmierId] = useState<string>('');
  const [infirmierInfo, setInfirmierInfo] = useState<any>(null);
  const [fichesSoins, setFichesSoins] = useState<FicheSoin[]>([]);
  const [selectedFiche, setSelectedFiche] = useState<FicheSoin | null>(null);
  const [surveillances, setSurveillances] = useState<FicheSurveillance[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Form for adding surveillance entry
  const [formData, setFormData] = useState({
    tension: '',
    temperature: '',
    pouls: '',
    saturation: '',
    conscience: 'conscient',
    traitement_adminstre: '',
    observations: '',
  });
  const { mutate: offlineMutate } = useOfflineMutation<any>();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'infirmier') { navigate('/', { replace: true }); return; }
    loadInfirmierInfo();
  }, [user, role, authLoading]);

  const loadInfirmierInfo = async () => {
    const { data } = await supabase.from('infirmiers').select('*').eq('user_id', user!.id).maybeSingle();
    if (data) {
      setInfirmierId(data.id);
      setInfirmierInfo(data);
      loadFichesSoins();
    }
  };

  const loadFichesSoins = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('fiches_soins')
      .select('*, patients(nom, prenom, numero_dossier), medecins(nom, prenom, specialite)')
      .order('date_soin', { ascending: false })
      .limit(50);

    if (data) setFichesSoins(data);
    setLoading(false);
  };

  const selectFiche = async (fiche: FicheSoin) => {
    setSelectedFiche(fiche);
    setSuccess('');
    setError('');
    const { data } = await supabase
      .from('fiches_surveillance')
      .select('*, infirmiers(nom, prenom)')
      .eq('fiche_soin_id', fiche.id)
      .order('date_heure', { ascending: false });

    if (data) setSurveillances(data);
  };

  const addSurveillanceEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFiche || !infirmierId) return;
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const insertData = {
        fiche_soin_id: selectedFiche.id,
        patient_id: selectedFiche.patient_id,
        medecin_traitant_id: selectedFiche.medecin_id,
        infirmier_id: infirmierId,
        tension: formData.tension || null,
        temperature: formData.temperature ? parseFloat(formData.temperature) : null,
        pouls: formData.pouls ? parseInt(formData.pouls) : null,
        saturation: formData.saturation ? parseInt(formData.saturation) : null,
        conscience: formData.conscience,
        traitement_adminstre: formData.traitement_adminstre || null,
        observations: formData.observations || null,
      };

      await offlineMutate({
        table: 'fiches_surveillance',
        operation: 'INSERT',
        data: insertData,
        metadata: { summary: `Surveillance — ${selectedFiche.patients?.nom || 'Patient'}` },
        onlineExecutor: async () => {
          const { data, error: insertErr } = await supabase.from('fiches_surveillance').insert(insertData).select().single();
          if (insertErr) throw insertErr;
          return data;
        },
      });

      setSuccess('Surveillance enregistrée avec succès !');
      setFormData({ tension: '', temperature: '', pouls: '', saturation: '', conscience: 'conscient', traitement_adminstre: '', observations: '' });

      const { data } = await supabase
        .from('fiches_surveillance')
        .select('*, infirmiers(nom, prenom)')
        .eq('fiche_soin_id', selectedFiche.id)
        .order('date_heure', { ascending: false });
      if (data) setSurveillances(data);

      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'enregistrement');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-clinic-blue focus:outline-none transition-colors text-sm bg-white";
  const labelClass = "block text-sm font-semibold text-gray-700 mb-2";

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin mb-4 block"></i>
          <p className="text-clinic-blue-dark text-xl">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFB]">
      <Header />

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-10">
        {/* Titre */}
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">Tableau de bord Infirmier</h1>
            <span className="rounded-full border border-clinic-blue/20 bg-clinic-blue/5 px-3 py-1 text-xs font-semibold text-clinic-blue-dark whitespace-nowrap">
              {infirmierInfo?.prenom} {infirmierInfo?.nom} — {infirmierInfo?.specialite || 'Soins généraux'}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500">Fiches de soins et surveillance des patients</p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Colonne gauche : Liste des fiches de soins */}
          <div className="lg:col-span-1">
            <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
              <div className="border-b border-gray-100 px-5 py-4">
                <h2 className="text-sm font-semibold text-gray-900">Fiches de soins</h2>
              </div>
              <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
                {fichesSoins.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <p className="text-sm">Aucune fiche de soin</p>
                  </div>
                ) : (
                  fichesSoins.map((f) => (
                    <div
                      key={f.id}
                      onClick={() => selectFiche(f)}
                      className={`p-4 cursor-pointer transition-all ${
                        selectedFiche?.id === f.id ? 'bg-clinic-blue/5 border-l-4 border-clinic-blue' : 'hover:bg-gray-50'
                      }`}
                    >
                      <p className="font-semibold text-sm text-gray-900">
                        {f.patients?.nom} {f.patients?.prenom}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        N° {f.patients?.numero_dossier || 'N/A'} • {f.date_soin ? new Date(f.date_soin).toLocaleDateString('fr-FR') : 'Date N/A'}
                      </p>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">{f.soins_a_administrer}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          f.statut === 'en_cours' ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'
                        }`}>
                          {f.statut === 'en_cours' ? 'En cours' : 'Terminé'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Colonne droite : Fiche de surveillance */}
          <div className="lg:col-span-2">
            {!selectedFiche ? (
              <div className="rounded-xl border border-gray-100 bg-white p-12 text-center text-gray-500">
                <p className="text-lg font-medium text-gray-700 mb-1">Sélectionnez une fiche de soin</p>
                <p className="text-sm">Pour remplir la fiche de surveillance correspondante</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Infos patient et médecin */}
                <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
                  <div className="border-b border-gray-100 px-5 py-4">
                    <h2 className="text-sm font-semibold text-gray-900">Fiche de surveillance</h2>
                  </div>
                  <div className="p-5 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-xs text-gray-500 mb-1">Patient</p>
                        <p className="font-semibold text-gray-900">{selectedFiche.patients?.nom} {selectedFiche.patients?.prenom}</p>
                        <p className="text-xs text-gray-500">N° {selectedFiche.patients?.numero_dossier || 'N/A'}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-xs text-gray-500 mb-1">Médecin traitant</p>
                        <p className="font-semibold text-gray-900">Dr. {selectedFiche.medecins?.nom} {selectedFiche.medecins?.prenom}</p>
                        <p className="text-xs text-gray-500">{selectedFiche.medecins?.specialite}</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-xs text-gray-500 mb-1">Soins prescrits</p>
                        <p className="text-sm text-gray-900">{selectedFiche.soins_a_administrer}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Formulaire de saisie */}
                <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
                  <div className="border-b border-gray-100 px-5 py-4">
                    <h3 className="text-sm font-semibold text-gray-900">Nouvelle saisie de surveillance</h3>
                  </div>
                  <div className="p-5">
                    {success && (
                      <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm">
                        {success}
                      </div>
                    )}
                    {error && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
                        {error}
                      </div>
                    )}

                    <form onSubmit={addSurveillanceEntry} className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        <div>
                          <label className={labelClass}>Tension</label>
                          <input type="text" value={formData.tension} onChange={(e) => setFormData({ ...formData, tension: e.target.value })} className={inputClass} placeholder="Ex: 12/8" />
                        </div>
                        <div>
                          <label className={labelClass}>Température (°C)</label>
                          <input type="number" step="0.1" value={formData.temperature} onChange={(e) => setFormData({ ...formData, temperature: e.target.value })} className={inputClass} placeholder="Ex: 37.5" />
                        </div>
                        <div>
                          <label className={labelClass}>Pouls (bpm)</label>
                          <input type="number" value={formData.pouls} onChange={(e) => setFormData({ ...formData, pouls: e.target.value })} className={inputClass} placeholder="Ex: 72" />
                        </div>
                        <div>
                          <label className={labelClass}>Saturation (%)</label>
                          <input type="number" value={formData.saturation} onChange={(e) => setFormData({ ...formData, saturation: e.target.value })} className={inputClass} placeholder="Ex: 98" />
                        </div>
                        <div>
                          <label className={labelClass}>Conscience</label>
                          <select value={formData.conscience} onChange={(e) => setFormData({ ...formData, conscience: e.target.value })} className={inputClass + ' appearance-none bg-white pr-8'}>
                            <option value="conscient">Conscient</option>
                            <option value="somnolent">Somnolent</option>
                            <option value="inconscient">Inconscient</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className={labelClass}>Traitement administré</label>
                        <textarea value={formData.traitement_adminstre} onChange={(e) => setFormData({ ...formData, traitement_adminstre: e.target.value })} className={inputClass + ' resize-none'} rows={2} placeholder="Médicaments administrés..." maxLength={500} />
                      </div>
                      <div>
                        <label className={labelClass}>Observations</label>
                        <textarea value={formData.observations} onChange={(e) => setFormData({ ...formData, observations: e.target.value })} className={inputClass + ' resize-none'} rows={2} placeholder="Observations..." maxLength={500} />
                      </div>
                      <button type="submit" disabled={saving} className="px-6 py-3 bg-clinic-blue text-white rounded-xl hover:bg-clinic-blue-dark transition-all font-semibold disabled:opacity-50 whitespace-nowrap cursor-pointer">
                        {saving ? 'Enregistrement...' : 'Enregistrer la surveillance'}
                      </button>
                    </form>
                  </div>
                </div>

                {/* Historique des surveillances */}
                <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
                  <div className="border-b border-gray-100 px-5 py-4">
                    <h3 className="text-sm font-semibold text-gray-900">Historique des surveillances ({surveillances.length})</h3>
                  </div>
                  <div className="p-5">
                    {surveillances.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p className="text-sm">Aucune surveillance enregistrée</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {surveillances.map((s) => (
                          <div key={s.id} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-sm font-semibold text-gray-900">
                                {s.date_heure ? new Date(s.date_heure).toLocaleString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Date N/A'}
                              </p>
                              {s.infirmiers && (
                                <span className="text-xs text-clinic-blue-dark font-medium">
                                  {s.infirmiers.prenom} {s.infirmiers.nom}
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                              {s.tension && <div><span className="text-gray-500">Tension:</span> <span className="font-medium">{s.tension}</span></div>}
                              {s.temperature && <div><span className="text-gray-500">Temp:</span> <span className="font-medium">{s.temperature}°C</span></div>}
                              {s.pouls && <div><span className="text-gray-500">Pouls:</span> <span className="font-medium">{s.pouls} bpm</span></div>}
                              {s.saturation && <div><span className="text-gray-500">Sat:</span> <span className="font-medium">{s.saturation}%</span></div>}
                              <div><span className="text-gray-500">Conscience:</span> <span className={`font-medium ${s.conscience === 'inconscient' ? 'text-red-600' : s.conscience === 'somnolent' ? 'text-amber-600' : 'text-green-600'}`}>{s.conscience}</span></div>
                            </div>
                            {s.traitement_adminstre && (
                              <div className="mt-2 text-sm">
                                <span className="text-gray-500">Traitement:</span> <span className="text-gray-900">{s.traitement_adminstre}</span>
                              </div>
                            )}
                            {s.observations && (
                              <div className="mt-1 text-sm text-gray-600">
                                <span className="text-gray-500">Obs:</span> {s.observations}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}